import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MagnifyingGlass, ArrowRight, MapPin, Storefront } from '@phosphor-icons/react';
import { fetchDiscover } from '../api/api';
import useDefaultZip from '../hooks/useDefaultZip';
import StoreDiscoverSection from '../components/discover/StoreDiscoverSection';
import { useAnalytics } from '../context/FirebaseContext';
import SEO from '../components/SEO/SEO';
import { SERVICE_AREA } from '../seo/site';

// The landing page is deliberately just "find something to order": search,
// browse stores, then each store's featured / most-ordered / most-rated food.
// The explainer content (how ordering works, what Zimzo is) lives on /about so
// it doesn't push the food below the fold. The headline is the page's one
// <h1> and carries the Ravangla wording search engines rank it on.
const Home = () => {
  const [discover, setDiscover] = useState([]);
  const [discoverLoading, setDiscoverLoading] = useState(true);
  const [query, setQuery] = useState('');
  const { trackPageView } = useAnalytics();
  const navigate = useNavigate();
  const defaultZip = useDefaultZip();

  useEffect(() => {
    trackPageView('/');
  }, [trackPageView]);

  // Re-fetched when the customer's pincode resolves, so the feed narrows to
  // stores that actually deliver to them once we know where they are. Starts
  // unfiltered so a logged-out visitor still sees a populated page.
  useEffect(() => {
    let cancelled = false;
    setDiscoverLoading(true);

    fetchDiscover({ pincode: defaultZip })
      .then((data) => {
        if (!cancelled) setDiscover(data.stores || []);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load discover feed', err);
      })
      .finally(() => {
        if (!cancelled) setDiscoverLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [defaultZip]);

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <>
      {/* Platform defaults from seo/site.js: the Ravangla food / café / pizza /
          grocery title and description. */}
      <SEO />

      <div className="bg-white">
        {/* Search + browse - the only two things above the feed */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-2 md:pt-10">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
            Food delivery in {SERVICE_AREA.place}
          </h1>
          <p className="text-sm md:text-base text-gray-500 mt-1 mb-4">
            Coffee, pizza, waffles, snacks and more from local stores in {SERVICE_AREA.place}, {SERVICE_AREA.region}.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 sm:max-w-xl">
              <label htmlFor="home-search" className="sr-only">
                Search for a dish
              </label>
              <div className="relative">
                <MagnifyingGlass
                  size={20}
                  weight="bold"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  id="home-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search for pizza, coffee, waffles..."
                  className="w-full min-h-12 pl-12 pr-24 rounded-full border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 min-h-10 px-4 rounded-full bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-500"
                >
                  Search
                </button>
              </div>
            </form>

            <Link
              to="/stores"
              className="inline-flex items-center justify-center gap-2 min-h-12 px-5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-500"
            >
              <Storefront size={18} weight="bold" /> Browse stores
            </Link>
          </div>
        </section>

        {/* Per-store product feed: each store leads with actual food - its
            featured picks, its most-ordered items and its most-rated items -
            and the store header links through to the full menu. */}
        <section className="pt-4 pb-10 md:pb-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-2">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                {defaultZip ? 'Delivering to you' : `Popular in ${SERVICE_AREA.place}`}
              </h2>
              <Link
                to="/stores"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors min-h-11"
              >
                All stores <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
            {defaultZip && (
              <p className="flex items-center gap-1.5 text-sm text-gray-500 mb-6">
                <MapPin size={14} weight="bold" /> Showing stores that deliver to {defaultZip}
              </p>
            )}

            {discoverLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 rounded-full border-t-2 border-b-2 border-amber-600" />
              </div>
            ) : discover.length === 0 ? (
              <p className="text-center text-gray-500 py-12">
                {defaultZip
                  ? `No stores deliver to ${defaultZip} yet — check back soon.`
                  : 'No stores available right now — check back soon.'}
              </p>
            ) : (
              <div>
                {discover.map((block) => (
                  <StoreDiscoverSection key={block.store.id} block={block} />
                ))}
              </div>
            )}

            <div className="text-center mt-8 sm:hidden">
              <Link
                to="/stores"
                className="inline-flex items-center gap-1 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors min-h-11"
              >
                View all stores <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
